import React, { useState, useEffect, createContext } from "react";

// api services
import { TriviaService } from "../services";

const TriviaContext = createContext({});

const Provider = ({ children }) => {
  const [trivia, setTrivia] = useState();
  const [gameId, setGameId] = useState();
  const [activeTrivia, setActiveTrivia] = useState();
  const [activeTriviaCategory, setActiveTriviaCategory] = useState();
  const [questions, setQuestions] = useState();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [answerResponse, setAnswerResponse] = useState();
  const [isProcessingReward, setIsProcessingReward] = useState(false);
  const [redemptionData, setRedemptionData] = useState();
  const [historyData, setHistoryData] = useState();
  const [activeRewardScheme, setActiveRewardScheme] = useState();
  const [activeVoucher, setActiveVoucher] = useState();
  const [locationSelected, setIsLocationSelected] = useState(false);
  const [openPlayTriviaModal, setOpenPlayTriviaModal] = useState(false);
  const [isLoadingTrivia, setIsLoadingTrivia] = useState(false);
  const [triviaRating, setTriviaRatingModal] = useState(true);
  const [userIdentification, setUserIdentification] = useState(null);
  
  const [rewardAvailability, setRewardAvailability] = useState();

  useEffect(() => {
    getTrivia();
    getHistory();
  }, []);

  return (
    <TriviaContext.Provider
      value={{
        getTrivia,
        gameId,
        trivia,
        setActiveTriviaCategory,
        activeTriviaCategory,
        setActiveTrivia,
        activeTrivia,
        questions,
        getQuestions,
        setQuestions,
        isSubmitting,
        submitAnswer,
        answerResponse,
        setAnswerResponse,
        saveTriviaVoucher,
        saveTriviaUserDisqualified,
        redeemReward,
        isProcessingReward,
        redemptionData,
        getHistory,
        historyData,
        setActiveRewardScheme,
        activeRewardScheme,
        locationSelected,
        setIsLocationSelected,
        openPlayTriviaModal,
        setOpenPlayTriviaModal,
        isLoadingTrivia,
        setIsLoadingTrivia,
        activeVoucher,
        setActiveVoucher,
        redeemRewardOnground,
        triviaRating,
        setUserIdentification,
        setTriviaRatingModal,
        rewardAvailability,
        checkRewardAvailability,
      }}
    >
      {children}
    </TriviaContext.Provider>
  );

  async function saveTriviaVoucher(voucher, triviaId) {
    try {
      const token = JSON.parse(localStorage.getItem("token"));

      const { data } = await TriviaService.postTriviaVoucher({
        token,
        voucher,
        triviaId,
      });

      getTrivia();
    } catch (e) { }
  }

  async function saveTriviaUserDisqualified(triviaId) {
    try {
      const token = JSON.parse(localStorage.getItem("token"));

      const { data } = await TriviaService.postTriviaUserDisqualified({
        token,
        triviaId,
      });

      getTrivia();
    } catch (e) { }
  }

  async function getTrivia() {
    try {
      setIsLoadingTrivia(true);
      const token = JSON.parse(localStorage.getItem("token"));
      const { data, success } = await TriviaService.getTrivia(token);

      if (success) {
        const { gameId, trivia } = data;
        setTrivia(trivia);
        setGameId(gameId);
      }
    } catch (e) {
    } finally {
      setIsLoadingTrivia(false);
    }
  }

  async function getQuestions(id) {
    setQuestions(null);
    try {
      const token = JSON.parse(localStorage.getItem("token"));
      const { data, success } = await TriviaService.getQuestions(token, id);

      if (success) {
        const { questions } = data;
        setQuestions(questions);
      } else {
        setQuestions(false);
      }
    } catch (e) {
      setQuestions(false);
    }
  }

  async function submitAnswer(answerData) {
    setIsSubmitting(true);
    let updatedAnswerData = answerData;
    if (userIdentification) {
      updatedAnswerData = { ...answerData, userProvidedId: userIdentification }
    }
    try {
      const token = JSON.parse(localStorage.getItem("token"));
      const { data } = await TriviaService.submitAnswer(token, updatedAnswerData);

      setAnswerResponse(data);
    } catch (e) {
    } finally {
      setIsSubmitting(false);
    }
  }

  async function redeemReward({ rewardType, value, region, officeLocation }) {
    const { _id: triviaId } = activeTrivia;
    setIsProcessingReward(true);
    try {
      const token = JSON.parse(localStorage.getItem("token"));
      const { data } = await TriviaService.redeemReward({
        token,
        triviaId,
        rewardType,
        value,
        region,
        officeLocation
      });
      setRedemptionData(data);
      setActiveTrivia({
        ...activeTrivia,
        userStats: {
          ...activeTrivia.userStats,
          wonData: {
            ...activeTrivia.userStats?.wonData,
            rewardType,
            voucher: rewardType,
            pickupLocation: officeLocation,
            pickupRegion: region,
            value,
          }
        }
      })
    } catch (e) {
    } finally {
      setIsProcessingReward(false);
    }
  }

  async function redeemRewardOnground({ redemptionCode, callback }) {
    const { _id: triviaId } = activeTrivia;
    setIsProcessingReward(true);
    try {
      const token = JSON.parse(localStorage.getItem("token"));
      const data = await TriviaService.redeemRewardOnground({
        token,
        triviaId,
        redemptionCode,
      });
      if (callback) {
        callback(data);
      }

    } catch (e) {
      if (callback) {
        callback({
          success: false,
          message: e?.data?.message || e?.message || "An unexpected error occurred!"
        })
      }
    } finally {
      setIsProcessingReward(false);
    }
  }

  async function getHistory() {
    try {
      const token = JSON.parse(localStorage.getItem("token"));
      const { data } = await TriviaService.getHistory(token);

      setHistoryData(data);
    } catch (e) { }
  }

  async function checkRewardAvailability(triviaId) {
    try {
      setRewardAvailability({ isLoading: true })
      const token = JSON.parse(localStorage.getItem("token"));
      const { data } = await TriviaService.fetchRewardAvailability({ token, triviaId });

      setRewardAvailability({
        data,
      });
    } catch (e) { }
    finally {
      setRewardAvailability((prev) => ({
        ...prev, isLoading: false
      }))
    }
  }
};

TriviaContext.ProviderWrapper = Provider;

export default TriviaContext;
